import React, { useState } from 'react';
import FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { Workbook  } from 'exceljs';
import { jsPDF } from 'jspdf';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { sanitizeFilename } from '../../scripts/document.filename.script';
import { GlobalSwal } from '../../styles/swal.style';
import { CompanyToken } from '../../models/company.model';
import { Contact } from '../../models/contact.model';
import { legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { formatContact, formatLocation } from '../../scripts/format.value.script';
import { contactTypeValue } from '../../libraries/contact.type.library';

import AppInputExportType from '../element/InputExportType';
import logo from '../../assets/themes/svg/logo-official.png';

export interface AppModalContactExportProps {
  companyForUser: CompanyToken | null,
  contacts: Contact[] | null
};

const AppModalContactExport: React.FunctionComponent<AppModalContactExportProps> = ({companyForUser, contacts}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [formatExport, setFormatExport] = useState({value: '', valid: false})

  const fileExcel = async (company: CompanyToken, tableTitle: string[][], tableData: any[][]) => {
    let workbook = new Workbook()
    let worksheet = workbook.addWorksheet('Data')

    worksheet.addRow([])
    worksheet.mergeCells(`A1:F1`)
    worksheet.getRow(1).height = 35

    let imageBuffer = await axios.get(logo, {responseType: 'arraybuffer'})

    let imageLogotipo = workbook.addImage({
      buffer: imageBuffer.data,
      extension: 'png'
    })

    worksheet.addImage(imageLogotipo, { tl: {col: 0.1, row: 0.2}, ext: {width: 100, height: 30}})

    let titleRow = worksheet.addRow([`${company.name} - ${lang.labels.listContacts}`])
    titleRow.font = {size: 18, bold: true}
    titleRow.getCell(1).alignment = {vertical: 'distributed', horizontal: 'center'}

    worksheet.mergeCells(`A${titleRow.number}:F${titleRow.number}`)
    worksheet.getRow(2).height = 30

    worksheet.addRow([])
    worksheet.mergeCells(`A3:F3`)
    worksheet.getRow(3).height = 10

    let headerRow = worksheet.addRow(tableTitle[0])
    headerRow.font = {bold: true, color: {argb: 'FFFFFF'}}
    headerRow.eachCell( (cell) => {
      cell.alignment = {vertical: 'distributed', horizontal: 'left'}
      cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: '2980BA'}, bgColor: {argb: '2980BA'}}
    })

    worksheet.getRow(4).height = 20

    tableData.forEach((item) => { worksheet.addRow(item) })

    worksheet.getColumn(1).width = 35;
    worksheet.getColumn(2).width = 10;
    worksheet.getColumn(3).width = 25;
    worksheet.getColumn(4).width = 15;
    worksheet.getColumn(5).width = 40;
    worksheet.getColumn(6).width = 40;

    workbook.xlsx.writeBuffer().then( data => {
      let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      FileSaver.saveAs(blob, `Contacts_of_${company.name.replaceAll(' ', '_')}.xlsx`);
    })
  }

  const filePdf = async (company: CompanyToken, tableTitle: string[][], tableData: any[][]) => {
    let document = new jsPDF('landscape', 'mm', [340, 220])

    document.addImage(logo, 'PNG', 290, 5, 30, 9)

    document.setFontSize(18)
    document.setFont('helvetica', 'bold')
    document.text(`${company.name} - ${lang.labels.listContacts}`, 25, 20)

    autoTable(document, {head: tableTitle, body: tableData, startY: 25})

    document.save(`Contacts_of_${sanitizeFilename(company.name).replaceAll(' ', '_')}.pdf`)
  }

  const executeContactExport = () => {
    setLoadIndicator('on')

    if (companyForUser && contacts && formatExport.valid) {
      let tableTitle: string[][] = [[lang.labels.user.toUpperCase(), lang.labels.type.toUpperCase(), lang.labels.location.toUpperCase(), lang.labels.phone.toUpperCase(), lang.labels.email.toUpperCase(), lang.labels.webSite.toUpperCase()]]
      let tableData: any[][]  = [[]]

      tableData.pop()

      for (let contact of contacts) {
        tableData.push([formatContact(contact), contactTypeValue(lang, contact.type), formatLocation(lang,contact.data.address), contact.data.phone, contact.data.email, contact.data.website])
      }

      setTimeout( () => {
        switch (formatExport.value) {
          case 'excel':
            fileExcel(companyForUser, tableTitle, tableData)
            break;
          case  'pdf':
            filePdf(companyForUser, tableTitle, tableData)
            break;
        }

        setLoadIndicator('off')
        executeHideModalContactExport()
      }, 1000)
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
        legendInvalid('modal-contact-export-legend-format-export-type-required')
      })
    }
  }

  const executeHideModalContactExport = () => {
    modalHide('modal-contact-export')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-contact-export-legend-format-export-type-required')
    setFormatExport({value: '', valid: false})
  }

  return (
    <div id="modal-contact-export" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.exportContacts}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="fv-row">
                <label className="form-label required">{lang.labels.selectExportFormat}:</label>
                <AppInputExportType component={"modal-contact-export"} formatExport={formatExport} setFormatExport={setFormatExport}></AppInputExportType>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalContactExport}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={executeContactExport}>
              <span className="indicator-label">{lang.labels.export}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalContactExport;
